import { useGoogleMap } from '@react-google-maps/api';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { format, fromUnixTime } from 'date-fns';

type RainviewerResponse =
  | number[]
  | {
      radar?: {
        past?: Array<{ time?: number }>;
        nowcast?: Array<{ time?: number }>;
      };
    };

const isValidTimestamp = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const normalizeTimestamps = (json: RainviewerResponse): number[] => {
  if (Array.isArray(json)) {
    return json.filter(isValidTimestamp);
  }

  const past = json?.radar?.past ?? [];
  const nowcast = json?.radar?.nowcast ?? [];
  return [...past, ...nowcast]
    .map((item) => item?.time)
    .filter(isValidTimestamp);
};

const getTimestamps = async (): Promise<number[]> => {
  const data = await fetch('https://api.rainviewer.com/public/maps.json');
  const json = (await data.json()) as RainviewerResponse;
  return normalizeTimestamps(json);
};

const useTimestamps = () => {
  return useQuery('timestamps', getTimestamps, {
    refetchInterval: 600000, // 60000(1min) * 10
  });
};

export const useRain = () => {
  const map = useGoogleMap();

  const { data } = useTimestamps();

  const [isShow, setIsShow] = useState(true);

  useEffect(() => {
    if (map && data && data.length > 0) {
      const latestTimestamp = data[data.length - 1];

      map.overlayMapTypes.clear();

      const rainTiles = new google.maps.ImageMapType({
        name: '雨',
        getTileUrl: (coord, zoom) =>
          `https://tilecache.rainviewer.com/v2/radar/${latestTimestamp}/256/${zoom}/${coord.x}/${coord.y}/2/1_1.png`,
        tileSize: new google.maps.Size(256, 256),
      });

      map.overlayMapTypes.push(rainTiles);
    }
  }, [data, map]);

  useEffect(() => {
    if (map && data) {
      const rainTiles = map.overlayMapTypes.getAt(0) as
        | google.maps.ImageMapType
        | undefined;

      rainTiles?.setOpacity(isShow ? 0.5 : 0);
    }
  }, [isShow, map, data]);

  const toggleRain = useCallback(() => {
    setIsShow((p) => !p);
  }, []);

  const time =
    data && data.length > 0
      ? format(fromUnixTime(data[data.length - 1]), 'yy/MM/dd HH:mm')
      : 'データなし';

  return { toggleRain, isShow, time };
};
